import { useCallback, useEffect, useMemo, useRef, useState, type Dispatch, type SetStateAction } from 'react';

export const SESSION_PAGE_STATE_PREFIX = 'zeroth-session-page-state';
export const SESSION_PAGE_STATE_USER_KEY = `${SESSION_PAGE_STATE_PREFIX}:user`;
export const SESSION_PAGE_STATE_TTL_MS = 30 * 60 * 1000;

export interface SessionPageStateScope {
  page: string;
  state?: string;
  version: number;
  storeId?: string | null;
  userId?: string | null;
}

interface SessionPageStateEnvelope<T> {
  version: number;
  savedAt: number;
  expiresAt: number;
  value: T;
}

interface SessionPageStateStorageOptions {
  storage?: Storage;
  ttlMs?: number;
  now?: () => number;
}

interface UseSessionPageStateOptions<T> extends SessionPageStateStorageOptions {
  scope: SessionPageStateScope;
  initialState: T | (() => T);
  enabled?: boolean;
  restore?: boolean;
}

interface UseSessionPageStateMeta {
  clear: () => void;
  storageKey: string;
}

function encodeKeyPart(value: string): string {
  return encodeURIComponent(value);
}

function resolveInitialState<T>(initialState: T | (() => T)): T {
  return typeof initialState === 'function'
    ? (initialState as () => T)()
    : initialState;
}

export function getSessionPageStateStorage(): Storage | undefined {
  if (typeof window === 'undefined') {
    return undefined;
  }

  try {
    return window.sessionStorage;
  } catch {
    return undefined;
  }
}

export function buildSessionPageStateKey(scope: SessionPageStateScope): string {
  return [
    SESSION_PAGE_STATE_PREFIX,
    `v${scope.version}`,
    `user:${encodeKeyPart(scope.userId ?? 'session')}`,
    `store:${encodeKeyPart(scope.storeId ?? 'global')}`,
    `page:${encodeKeyPart(scope.page)}`,
    `state:${encodeKeyPart(scope.state ?? 'state')}`,
  ].join(':');
}

function isEnvelope<T>(value: unknown): value is SessionPageStateEnvelope<T> {
  if (typeof value !== 'object' || value === null) {
    return false;
  }

  const candidate = value as Partial<SessionPageStateEnvelope<T>>;
  return (
    typeof candidate.version === 'number'
    && typeof candidate.savedAt === 'number'
    && typeof candidate.expiresAt === 'number'
    && 'value' in candidate
  );
}

export function readSessionPageState<T>(
  scope: SessionPageStateScope,
  options: SessionPageStateStorageOptions = {}
): T | undefined {
  const storage = options.storage ?? getSessionPageStateStorage();
  if (!storage) {
    return undefined;
  }

  const key = buildSessionPageStateKey(scope);
  let raw: string | null;
  try {
    raw = storage.getItem(key);
  } catch {
    return undefined;
  }

  if (!raw) {
    return undefined;
  }

  const now = options.now?.() ?? Date.now();
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (!isEnvelope<T>(parsed) || parsed.version !== scope.version || parsed.expiresAt <= now) {
      try {
        storage.removeItem(key);
      } catch {
        // Ignore storage cleanup failures.
      }
      return undefined;
    }

    return parsed.value;
  } catch {
    try {
      storage.removeItem(key);
    } catch {
      // Ignore storage cleanup failures.
    }
    return undefined;
  }
}

export function writeSessionPageState<T>(
  scope: SessionPageStateScope,
  value: T,
  options: SessionPageStateStorageOptions = {}
): void {
  const storage = options.storage ?? getSessionPageStateStorage();
  if (!storage) {
    return;
  }

  const now = options.now?.() ?? Date.now();
  const ttlMs = options.ttlMs ?? SESSION_PAGE_STATE_TTL_MS;
  const envelope: SessionPageStateEnvelope<T> = {
    version: scope.version,
    savedAt: now,
    expiresAt: now + ttlMs,
    value,
  };

  try {
    storage.setItem(buildSessionPageStateKey(scope), JSON.stringify(envelope));
  } catch {
    // Storage writes can fail in private browsing or quota pressure. Page state should still work in memory.
  }
}

export function removeSessionPageState(
  scope: SessionPageStateScope,
  storage: Storage | undefined = getSessionPageStateStorage()
): void {
  try {
    storage?.removeItem(buildSessionPageStateKey(scope));
  } catch {
    // Ignore storage cleanup failures.
  }
}

export function clearSessionPageStates(storage: Storage | undefined = getSessionPageStateStorage()): void {
  if (!storage) {
    return;
  }

  for (let index = storage.length - 1; index >= 0; index -= 1) {
    try {
      const key = storage.key(index);
      if (key?.startsWith(SESSION_PAGE_STATE_PREFIX)) {
        storage.removeItem(key);
      }
    } catch {
      return;
    }
  }
}

export function syncSessionPageStateUserMarker(
  userId: string | null | undefined,
  storage: Storage | undefined = getSessionPageStateStorage()
): boolean {
  if (!storage) {
    return false;
  }

  const nextUserId = userId ?? null;
  let previousUserId: string | null;
  try {
    previousUserId = storage.getItem(SESSION_PAGE_STATE_USER_KEY);
  } catch {
    return false;
  }

  if (previousUserId === nextUserId) {
    return false;
  }

  clearSessionPageStates(storage);

  if (nextUserId) {
    try {
      storage.setItem(SESSION_PAGE_STATE_USER_KEY, nextUserId);
    } catch {
      // Ignore marker write failures.
    }
  }

  return true;
}

export function useSessionPageState<T>({
  scope,
  initialState,
  enabled = true,
  restore = true,
  storage,
  ttlMs,
  now,
}: UseSessionPageStateOptions<T>): [T, Dispatch<SetStateAction<T>>, UseSessionPageStateMeta] {
  const initialStateRef = useRef(initialState);
  initialStateRef.current = initialState;

  const memoizedScope = useMemo<SessionPageStateScope>(() => ({
    page: scope.page,
    state: scope.state,
    version: scope.version,
    storeId: scope.storeId,
    userId: scope.userId,
  }), [
    scope.page,
    scope.state,
    scope.storeId,
    scope.userId,
    scope.version,
  ]);
  const storageKey = useMemo(() => buildSessionPageStateKey(memoizedScope), [memoizedScope]);

  const readInitialState = useCallback(() => {
    if (enabled && restore) {
      const restored = readSessionPageState<T>(memoizedScope, { storage, ttlMs, now });
      if (restored !== undefined) {
        return restored;
      }
    }

    return resolveInitialState(initialStateRef.current);
  }, [enabled, memoizedScope, now, restore, storage, ttlMs]);

  const [state, setState] = useState<T>(() => readInitialState());
  const previousStorageKeyRef = useRef(storageKey);

  useEffect(() => {
    if (previousStorageKeyRef.current === storageKey) {
      return;
    }

    previousStorageKeyRef.current = storageKey;
    setState(readInitialState());
  }, [readInitialState, storageKey]);

  useEffect(() => {
    if (!enabled) {
      return;
    }

    writeSessionPageState(memoizedScope, state, { storage, ttlMs, now });
  }, [enabled, memoizedScope, now, state, storage, ttlMs]);

  const clear = useCallback(() => {
    removeSessionPageState(memoizedScope, storage);
    setState(resolveInitialState(initialStateRef.current));
  }, [memoizedScope, storage]);

  return [state, setState, { clear, storageKey }];
}

export function useSessionPageStateUserIsolation(
  userId: string | null | undefined,
  isAuthResolved: boolean
): void {
  useEffect(() => {
    if (!isAuthResolved) {
      return;
    }

    syncSessionPageStateUserMarker(userId);
  }, [isAuthResolved, userId]);
}
